// FILE: src/components/QuestionFactory/PipelineItemDetailModal.js
//
// Phase 2c — admin review modal for a single Question Factory draft.
//
// Shows the raw model output PLUS the structural validation result PLUS
// the AI rubric scores (per reviewer) PLUS the publish recommendation,
// so the admin can judge an item end-to-end without leaving the modal.
//
// Dismissed by clicking the backdrop, close button, or pressing Esc.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { publishItem, rejectItem } from '../../services/questionFactoryService';
import EditDraftModal from './EditDraftModal';

const STYLES = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0, 0, 0, 0.65)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '32px 16px', overflow: 'auto', zIndex: 1000,
  },
  shell: {
    background: '#15152a', border: '1px solid #2a2a4a', borderRadius: '8px',
    width: '100%', maxWidth: '920px', color: '#eee',
    padding: '24px 28px', boxSizing: 'border-box',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  title:  { fontSize: '1.1rem', fontWeight: 600, flex: 1 },
  closeBtn: {
    background: 'transparent', border: '1px solid #555', color: '#ccc',
    borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '0.85rem',
  },
  meta: { fontSize: '0.78rem', color: '#aab', marginBottom: '12px',
          display: 'flex', gap: '10px', flexWrap: 'wrap' },
  pill:  { padding: '2px 10px', borderRadius: '999px', border: '1px solid #303060', background: '#1a1a2e' },
  pillGood: { padding: '2px 10px', borderRadius: '999px', background: '#0f2a14', border: '1px solid #2e6a3e', color: '#a4f0b3' },
  pillWarn: { padding: '2px 10px', borderRadius: '999px', background: '#2e2a0f', border: '1px solid #6a663e', color: '#f0e6a4' },
  pillBad:  { padding: '2px 10px', borderRadius: '999px', background: '#2e1a1a', border: '1px solid #6a3030', color: '#ff9b9b' },
  sectionTitle: { fontSize: '0.75rem', fontWeight: 600, color: '#aab',
                  textTransform: 'uppercase', letterSpacing: '0.6px',
                  marginTop: '20px', marginBottom: '8px' },
  card: { background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '6px', padding: '12px 14px', fontSize: '0.88rem', lineHeight: 1.5 },
  rubricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '6px',
    fontSize: '0.78rem',
  },
  rubricCell: { background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '4px', padding: '6px 8px' },
  rubricLabel: { color: '#aab', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.3px' },
  rubricScore: { color: '#fff', fontWeight: 700, fontSize: '0.95rem' },
  choice: { display: 'flex', gap: '10px', alignItems: 'flex-start',
            padding: '8px 12px', borderRadius: '6px', marginBottom: '6px',
            background: '#0f0f1e', border: '1px solid #2a2a4a' },
  choiceCorrect: { background: '#0f1f0f', border: '1px solid #305a30' },
  choiceLabel: { fontWeight: 700, width: '20px', flexShrink: 0 },
  choiceMark: { color: '#7be88c', marginLeft: '8px' },
  reviewerCard: { background: '#101025', border: '1px solid #2a2a4a', borderRadius: '6px', padding: '10px 12px', marginBottom: '10px' },
  reviewerHeader: { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' },
  reviewerName:   { fontWeight: 700, color: '#fff' },
  reviewerMeta:   { fontSize: '0.72rem', color: '#aab' },
  reviewerComments: { marginTop: '8px', color: '#ddd', fontSize: '0.82rem', whiteSpace: 'pre-wrap' },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '8px',
    background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '6px',
    padding: '12px 14px', fontSize: '0.8rem',
  },
  metaKey:   { color: '#aab', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '0.65rem', marginBottom: '2px' },
  metaValue: { color: '#ddd', wordBreak: 'break-word' },
  actionRow: { display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' },
  approveBtn: { padding: '8px 16px', background: '#2e5a3a', border: '1px solid #4a9a5a',
                borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 },
  rejectBtn:  { padding: '8px 16px', background: '#5a2e2e', border: '1px solid #9a4a4a',
                borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.88rem' },
  editBtn:    { padding: '8px 16px', background: '#3a3a6e', border: '1px solid #5a5a9e',
                borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.88rem' },
  disabledBtn:{ opacity: 0.5, cursor: 'not-allowed' },
  busyMsg: { fontSize: '0.85rem', color: '#aab', marginTop: '8px' },
  publishedNote: { fontSize: '0.85rem', color: '#7be88c', marginTop: '8px' },
  errorBox: { background: '#1a0f1f', border: '1px solid #5a305a', borderRadius: '6px',
              padding: '12px 14px', fontSize: '0.82rem', color: '#ffb6ff', marginBottom: '10px',
              whiteSpace: 'pre-wrap' },
};

const PUBLISH_LABEL = {
  auto_publish:     { label: 'Auto Publish Eligible', style: 'pillGood' },
  stage_for_review: { label: 'Stage for Review',     style: 'pillGood' },
  manual_review:    { label: 'Manual Review',        style: 'pillWarn' },
  reject:           { label: 'Reject',               style: 'pillBad'  },
};

function fmt(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}
function fmtScore(v, suffix = '') {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}${suffix}`;
}
function correctSet(payload) {
  const ca = payload?.correctAnswers;
  if (!Array.isArray(ca)) return new Set();
  return new Set(ca.map(String));
}

export default function PipelineItemDetailModal({ item, onClose, onOpenInEditor, onPublished }) {
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);   // 'approve' | 'reject' | null
  const [actionError, setActionError] = useState(null);
  const [actionResult, setActionResult] = useState(null);  // { type, message }
  const [editing, setEditing] = useState(false);

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget && typeof onClose === 'function') onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && typeof onClose === 'function') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fetch the AI reviews directly from the question_ai_reviews table.
  // Reads through the same SECURITY DEFINER RPC pattern via PostgREST is
  // overkill for a read; we can hit the table with the user's JWT since
  // RLS only blocks anonymous role. (Authenticated admins can read; the
  // table is admin-only by virtue of the surrounding RPCs that write it.)
  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    setLoadingReviews(true);
    (async () => {
      const { data, error } = await supabase
        .from('question_ai_reviews')
        .select('*')
        .eq('item_id', item.id)
        .order('created_at', { ascending: true });
      if (!cancelled) {
        if (!error && Array.isArray(data)) setReviews(data);
        setLoadingReviews(false);
      }
    })();
    return () => { cancelled = true; };
  }, [item?.id]);

  const rawPayload = item?.raw_payload || {};
  const choices    = Array.isArray(rawPayload.choices) ? rawPayload.choices : [];
  const correct    = correctSet(rawPayload);
  const metadata   = rawPayload.metadata || {};
  const validationErrors = Array.isArray(item?.validation_errors) ? item.validation_errors : [];

  const publishRecommendation = item?.publish_recommendation || null;
  const pubInfo = publishRecommendation ? PUBLISH_LABEL[publishRecommendation] : null;

  const overallQualityScore = useMemo(() => {
    if (item?.consensus_score == null) return null;
    return Math.round(Number(item.consensus_score) * 10 * 100) / 100;
  }, [item]);

  if (!item) return null;

  return (
    <div style={STYLES.backdrop} onClick={handleBackdrop} data-testid="qf-item-modal">
      <div style={STYLES.shell} role="dialog" aria-label="Draft question detail">
        <div style={STYLES.header}>
          <div style={STYLES.title}>Draft {fmt(rawPayload.id, item.id?.slice(0, 8) + '…')}</div>
          <button data-testid="qf-item-modal-close" style={STYLES.closeBtn} onClick={onClose}>Close</button>
        </div>

        <div style={STYLES.meta}>
          <span style={STYLES.pill}>stage: <b>{item.pipeline_status}</b></span>
          <span style={item.validation_status === 'pass' ? STYLES.pillGood :
                        item.validation_status === 'review' ? STYLES.pillWarn :
                        item.validation_status === 'fail'   ? STYLES.pillBad  : STYLES.pill}>
            validation: {fmtScore(item.validation_score)} {item.validation_status ? `(${item.validation_status})` : ''}
          </span>
          {item.consensus_score != null && (
            <span style={overallQualityScore >= 90 ? STYLES.pillGood :
                          overallQualityScore >= 70 ? STYLES.pillWarn : STYLES.pillBad}>
              consensus: {fmtScore(item.consensus_score)} ({overallQualityScore != null ? overallQualityScore.toFixed(0) : '—'}/100)
            </span>
          )}
          {item.coaching_value != null && (
            <span style={STYLES.pill}>coaching value: <b>{fmtScore(item.coaching_value)}</b></span>
          )}
          {pubInfo && (
            <span style={STYLES[pubInfo.style]} data-testid="qf-item-publish-recommendation">
              {pubInfo.label}
            </span>
          )}
          {item.admin_decision && (
            <span style={item.admin_decision === 'rejected' ? STYLES.pillBad : STYLES.pillGood}>
              decision: {item.admin_decision}
            </span>
          )}
        </div>

        {validationErrors.length > 0 && (
          <div style={STYLES.errorBox}>
            <strong>Validation issues:</strong>
            {validationErrors.map((err, i) => <div key={i}>• {String(err)}</div>)}
          </div>
        )}

        <div style={STYLES.sectionTitle}>Question stem</div>
        <div style={STYLES.card}>{fmt(rawPayload.question, '(no question text)')}</div>

        <div style={STYLES.sectionTitle}>Choices</div>
        <div>
          {choices.length === 0 && <div style={STYLES.card}>(no choices in payload)</div>}
          {choices.map((c) => {
            const isCorrect = correct.has(String(c.id));
            return (
              <div key={c.id || c.text} data-testid={`qf-item-choice-${c.id || ''}`}
                   style={isCorrect ? { ...STYLES.choice, ...STYLES.choiceCorrect } : STYLES.choice}>
                <span style={STYLES.choiceLabel}>{c.id || '?'}</span>
                <span>{c.text}</span>
                {isCorrect && <span style={STYLES.choiceMark}>✓ correct</span>}
              </div>
            );
          })}
        </div>

        <div style={STYLES.sectionTitle}>Explanation</div>
        <div style={STYLES.card}>{fmt(rawPayload.explanation, '(no explanation)')}</div>

        {/* ── AI Rubric reviews (Phase 2c) ─────────────────────────────── */}
        <div style={STYLES.sectionTitle}>AI Adjudication ({reviews.length} {reviews.length === 1 ? 'reviewer' : 'reviewers'})</div>
        {loadingReviews && <div style={{ color: '#888', fontStyle: 'italic' }}>Loading reviews…</div>}
        {!loadingReviews && reviews.length === 0 && (
          <div style={{ color: '#888', fontStyle: 'italic' }}>No AI reviews yet for this item.</div>
        )}
        {reviews.map((r) => {
          const rubric = r.rubric || {};
          const overall = Number(rubric.overallQualityScore);
          const pub = rubric.publishRecommendation || null;
          const pubLabel = pub ? PUBLISH_LABEL[pub] : null;
          return (
            <div key={r.id} style={STYLES.reviewerCard} data-testid={`qf-review-${r.id}`}>
              <div style={STYLES.reviewerHeader}>
                <span style={STYLES.reviewerName}>{r.reviewer}</span>
                <span style={STYLES.reviewerMeta}>{r.reviewer_model}</span>
                {Number.isFinite(overall) && (
                  <span style={overall >= 90 ? STYLES.pillGood : overall >= 70 ? STYLES.pillWarn : STYLES.pillBad}>
                    overall: {overall.toFixed(0)}/100
                  </span>
                )}
                {pubLabel && <span style={STYLES[pubLabel.style]}>{pubLabel.label}</span>}
              </div>
              <div style={STYLES.rubricGrid}>
                {[
                  ['Technical Accuracy (25%)', rubric.technicalAccuracy],
                  ['Single Best (20%)',         rubric.singleBestAnswer],
                  ['Exam Alignment (15%)',      rubric.examAlignment],
                  ['Cognitive Level (10%)',     rubric.cognitiveLevel],
                  ['Scenario (10%)',            rubric.scenarioQuality],
                  ['Distractor (10%)',          rubric.distractorQuality],
                  ['Writing (5%)',              rubric.writingQuality],
                  ['Explanation (5%)',          rubric.explanationQuality],
                  ['Coaching Value',            rubric.coachingValue ?? r.coaching_value],
                ].map(([label, val]) => (
                  <div key={label} style={STYLES.rubricCell}>
                    <div style={STYLES.rubricLabel}>{label}</div>
                    <div style={STYLES.rubricScore}>{val == null ? '—' : Number(val).toFixed(1)}</div>
                  </div>
                ))}
              </div>
              {r.comments && <div style={STYLES.reviewerComments}>“{r.comments}”</div>}
              {rubric.error && (
                <div style={{ ...STYLES.errorBox, marginTop: '8px' }}>
                  Reviewer error: {String(rubric.error)}
                </div>
              )}
            </div>
          );
        })}

        <div style={STYLES.sectionTitle}>Admin actions</div>
        {(() => {
          // Treat the item as published if either the DB row says so OR we just
          // published it from this modal (the actionResult flag lets us flip the
          // UI immediately without waiting for a re-fetch).
          const justPublished = actionResult?.type === 'approved';
          const isPublished   = item.pipeline_status === 'published' || justPublished;
          const publishedQuestionId =
            item.published_question_id || actionResult?.publishedQuestionId || null;

          if (isPublished) {
            return (
              <>
                <div style={STYLES.publishedNote}>
                  ✓ Promoted to live <code>questions</code> table as <code>{publishedQuestionId || '(no id)'}</code>
                </div>
                <div style={STYLES.actionRow}>
                  <span data-testid="qf-item-published-chip"
                        style={{ ...STYLES.approveBtn, ...STYLES.disabledBtn, cursor: 'default' }}>
                    ✓ Published
                  </span>
                  <button data-testid="qf-item-open-in-editor"
                          style={typeof onOpenInEditor === 'function' && publishedQuestionId
                            ? STYLES.editBtn : { ...STYLES.editBtn, ...STYLES.disabledBtn }}
                          disabled={typeof onOpenInEditor !== 'function' || !publishedQuestionId}
                          onClick={() => {
                            if (typeof onOpenInEditor === 'function' && publishedQuestionId) {
                              onOpenInEditor(publishedQuestionId);
                            }
                          }}>
                    Open in Question Editor →
                  </button>
                </div>
              </>
            );
          }

          const canPublish = ['adjudicated', 'staged', 'validated'].includes(item.pipeline_status);
          const canReject  = !['rejected'].includes(item.pipeline_status);
          return (
            <>
              <div style={STYLES.actionRow}>
                <button data-testid="qf-item-approve"
                        style={canPublish && !actionBusy ? STYLES.approveBtn : { ...STYLES.approveBtn, ...STYLES.disabledBtn }}
                        disabled={!canPublish || actionBusy}
                        onClick={async () => {
                          setActionBusy('approve'); setActionError(null);
                          try {
                            const r = await publishItem({ itemId: item.id });
                            setActionResult({
                              type: 'approved',
                              message: `Published as ${r?.question_id || 'question'}.`,
                              publishedQuestionId: r?.question_id || null,
                            });
                            if (typeof onPublished === 'function') onPublished();
                          } catch (e) { setActionError(e.message || String(e)); }
                          finally { setActionBusy(null); }
                        }}>
                  {actionBusy === 'approve' ? 'Publishing…' : 'Approve & Publish'}
                </button>
                <button data-testid="qf-item-reject"
                        style={canReject && !actionBusy ? STYLES.rejectBtn : { ...STYLES.rejectBtn, ...STYLES.disabledBtn }}
                        disabled={!canReject || actionBusy}
                        onClick={async () => {
                          if (!window.confirm('Reject this draft? It will not be available for publish later.')) return;
                          setActionBusy('reject'); setActionError(null);
                          try {
                            await rejectItem({ itemId: item.id });
                            setActionResult({ type: 'rejected', message: 'Item rejected.' });
                          } catch (e) { setActionError(e.message || String(e)); }
                          finally { setActionBusy(null); }
                        }}>
                  {actionBusy === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
                <button data-testid="qf-item-edit"
                        style={!actionBusy ? STYLES.editBtn : { ...STYLES.editBtn, ...STYLES.disabledBtn }}
                        disabled={actionBusy}
                        onClick={() => setEditing(true)}>
                  Edit draft JSON
                </button>
              </div>
              {actionError && <div style={STYLES.errorBox}>{actionError}</div>}
              {actionResult?.type === 'rejected' && (
                <div style={{ ...STYLES.busyMsg, color: '#e8d97b' }}>
                  {actionResult.message} Reload the run to see updated counters.
                </div>
              )}
              {actionResult?.type === 'edited' && (
                <div style={{ ...STYLES.busyMsg, color: '#7be88c' }}>
                  {actionResult.message}
                </div>
              )}
            </>
          );
        })()}

        <div style={STYLES.sectionTitle}>Question metadata</div>
        <div style={STYLES.metaGrid}>
          {Object.keys(metadata).length === 0 && <div style={STYLES.metaValue}>(no metadata block)</div>}
          {Object.entries(metadata).map(([k, v]) => (
            <div key={k}>
              <div style={STYLES.metaKey}>{k}</div>
              <div style={STYLES.metaValue}>{fmt(typeof v === 'object' ? JSON.stringify(v) : v)}</div>
            </div>
          ))}
        </div>
      </div>
      {editing && (
        <EditDraftModal
          item={item}
          onClose={() => setEditing(false)}
          onSaved={() => setActionResult({ type: 'edited', message: 'Draft updated.' })}
        />
      )}
    </div>
  );
}
